import { useEffect, useState } from "react";
import { caseListContainerClasses } from "./common/caseListClasses";
import { useNavigate } from 'react-router-dom';
import CaseNotes from "../pages/admin/discovery/component/CaseNotes";
import useAxios from '../hooks/useAxios';
import Constants from '../Constants';
import { toaster } from "../utils/toast";
import ChatWindow from "../pages/chatBot/ChatWindow";
import CaseSummaryHeader from './CaseSummaryHeader';
import CaseGapReviewPanel from './CaseGapReviewPanel';
import { getDisplayAmount } from '../Utils';
import FinalSettlementSec from "./FinalSettlementSec";
import RenderIf from "./render-if/Renderif";
import XLoader from "./common/XLoader";
import EditCaseButton from './ui-control/EditCaseButton';
import { useChatToggle } from '../contexts/ChatToggleContext';
import { partyCardFrameClasses } from './common/listItemClasses';
const partyHeaderClasses = "flex justify-between items-center bg-surface-alt px-4! py-2! rounded-t-md!";
const partyCardClasses = partyCardFrameClasses;
const detailPanelClasses = "bg-surface p-2! rounded! mt-1!";

const CaseSummary = ({ caseId }) => {
    const [showProviders, setShowProviders] = useState({});
    const [showInsurance, setShowInsurance] = useState({});
    const [showDefendantInsurance, setShowDefendantInsurance] = useState({});
    const [caseSummaryData, setCaseSummaryData] = useState({});
    const { isChatOpen } = useChatToggle();
    const { getData, isLoading } = useAxios();

    const navigate = useNavigate();

    useEffect(() => {
        const fetchCaseSummaryDetails = async () => {
            if (caseId) {
                try {
                    const response = await getData(`${Constants.ApiUrl.caseSummary.getCaseSummary}/${caseId}`);
                    if (!response.success) {
                        toaster({ message: response.message, success: response.success });
                        navigate("/cases");
                    }
                    setCaseSummaryData(response?.data);
                } catch (error) {
                    console.error('Error fetching case summary details:', error);
                }
            }
        };
        fetchCaseSummaryDetails();
    }, [caseId, getData, navigate]);



    return (
        <section className={`[&_.chatbot]:h-screen! [&_.chatbot]:pb-2.5 ${isChatOpen ? 'chat-open' : ''}`}>
            <ChatWindow caseId={caseId} isOpen={isChatOpen} />
            <div className={`container ${caseListContainerClasses}`}>
                <div className="listing">
                    <RenderIf shouldRender={isLoading}>
                        <div className='flex justify-center'>
                            <XLoader />
                        </div>
                    </RenderIf>
                    <RenderIf shouldRender={!isLoading}>
                        <>
                            <CaseSummaryHeader caseId={caseId} caseData={caseSummaryData} />
                            <CaseGapReviewPanel caseId={caseId} gapReport={caseSummaryData?.virtualUserGapReport} caseInfo={caseSummaryData?.caseInfo} />
                            <FinalSettlementSec caseId={caseId} caseData={caseSummaryData} />

                            <div className="row">
                                <div className="col-md-6">
                                    <div className={partyHeaderClasses}>
                                        <strong>Plaintiffs</strong>
                                    </div>
                                    {caseSummaryData?.clients?.map((client, i) => (
                                        <div key={i} className={partyCardClasses}>
                                            <div className="flex justify-between relative">
                                                <div>
                                                    <strong>{client?.personalInfo?.clientName}</strong> <span className="text-subtle">Age {client?.personalInfo?.age} | {client?.personalInfo?.gender}</span>
                                                </div>

                                                <div className="flex flex-col items-end absolute right-0 top-0 gap-2">
                                                    <EditCaseButton caseId={caseId} className="w-fit!" />
                                                </div>
                                            </div>
                                            <div className="text-subtle text-sm mt-2 my-2.5">{client?.personalInfo?.phoneNumber}</div>
                                            <div className="text-subtle text-sm my-2.5">{client?.personalInfo?.email}</div>
                                            <div
                                                className="text-subtle text-sm mt-2 my-2.5 cursor-pointer select-none"
                                                onClick={() => setShowInsurance((prev) => ({
                                                    ...prev,
                                                    [i]: !prev[i],
                                                }))}
                                            >
                                                Insurance: <span className="font-medium">{client?.insurance?.insuranceName}</span>
                                                <span className="inline-flex items-center justify-center w-5 h-2 mt-px text-primary! ml-2!">
                                                    <i className={`fas fa-chevron-${showInsurance[i] ? 'up' : 'down'}`}></i>
                                                </span>
                                            </div>
                                            {showInsurance[i] && (
                                                <div className={detailPanelClasses}>
                                                    <div className="text-subtle text-sm">
                                                        Claim No: <span>{client?.insurance?.claimNumber}</span>
                                                    </div>
                                                    <div className="text-subtle text-sm">
                                                        Name: <span>{client?.insurance?.adjuster?.name}</span> | Phone: <span>{client?.insurance?.adjuster?.phoneNumber}</span>
                                                    </div>
                                                    <div className="text-subtle text-sm">
                                                        Email: <span>{client?.insurance?.adjuster?.email}</span>
                                                    </div>
                                                    <div className="text-subtle text-sm">
                                                        Address: <span>{client?.insurance?.adjuster?.address}, {client?.insurance?.adjuster?.city}, {client?.insurance?.adjuster?.state}, {client?.insurance?.adjuster?.zip} </span>
                                                    </div>
                                                </div>
                                            )}
                                            <div className="text-subtle text-sm mt-2 my-2.5">
                                                Total Damages: <span className="font-medium">{(() => {
                                                    const clientData = caseSummaryData?.damageAmountPerClient?.find(c => c.clientId === client.clientId);
                                                    return clientData && getDisplayAmount(clientData.perClientDamageAmount);
                                                })()}</span>
                                            </div>
                                            <div
                                                className="text-subtle text-sm mt-2 my-2.5 cursor-pointer select-none"
                                                onClick={() => setShowProviders((prev) => ({
                                                    ...prev,
                                                    [i]: !prev[i],
                                                }))}
                                            >
                                                Medical Providers
                                                <span className="inline-flex items-center justify-center w-5 h-2 mt-px text-primary! ml-2!">
                                                    <i className={`fas fa-chevron-${showProviders[i] ? 'up' : 'down'}`}></i>
                                                </span>
                                            </div>
                                            {showProviders[i] && (
                                                <div className={detailPanelClasses}>
                                                    <EditCaseButton caseId={caseId} goToStep={2} />
                                                    <span className="ml-4!">
                                                        {client?.medicalProviders?.postAccident?.medicalRecords?.map((provider) => provider?.providerName).join(", ")}
                                                        {client?.medicalProviders?.preAccident?.medicalRecords?.map((provider) => provider?.providerName).join(", ")}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                <div className="col-md-6">
                                    <div className={partyHeaderClasses}>
                                        <strong>Defendants</strong>
                                    </div>
                                    {caseSummaryData?.defendants?.map((defendant, index) => (
                                        <div key={index} className={partyCardClasses}>
                                            <div className="flex justify-between items-start gap-3">
                                                <div>
                                                    <strong>{defendant?.personalInfo?.name}</strong>
                                                </div>
                                                <div className="flex justify-end">
                                                    <EditCaseButton caseId={caseId} className="w-fit!" />
                                                </div>
                                            </div>
                                            <div className="text-subtle text-sm my-2.5">Claim No: <span className="font-medium">{defendant?.insurance?.claimNumber}</span></div>
                                            <div
                                                className="text-subtle text-sm mt-2 my-2.5 cursor-pointer select-none"
                                                onClick={() => setShowDefendantInsurance((prev) => ({
                                                    ...prev,
                                                    [index]: !prev[index],
                                                }))}
                                            >
                                                Insurance: <span className="font-medium">{defendant?.insurance?.insuranceName}</span>
                                                <span className="inline-flex items-center justify-center w-5 h-2 mt-px text-primary! ml-2!">
                                                    <i className={`fas fa-chevron-${showDefendantInsurance[index] ? 'up' : 'down'}`}></i>
                                                </span>
                                            </div>
                                            {showDefendantInsurance[index] && (
                                                <div className={detailPanelClasses}>
                                                    <div className="text-subtle text-sm">
                                                        Name: <span>{defendant?.insurance?.adjuster?.name}</span> | Phone: <span>{defendant?.insurance?.adjuster?.phoneNumber}</span>
                                                    </div>
                                                    <div className="text-subtle text-sm">
                                                        Email: <span>{defendant?.insurance?.adjuster?.email}</span>
                                                    </div>
                                                    <div className="text-subtle text-sm">
                                                        Address: <span>{defendant?.insurance?.adjuster?.address}, {defendant?.insurance?.adjuster?.city}, {defendant?.insurance?.adjuster?.state}, {defendant?.insurance?.adjuster?.zip} </span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <CaseNotes caseId={caseId} noteData={caseSummaryData?.notesData} />
                        </>
                    </RenderIf>
                </div>
            </div>
        </section>
    )

}

export default CaseSummary;
